/* eslint-disable max-lines */
import {
  channelService,
  mailConfigService
} from 'shared/api';
import {
  Input
} from 'shared/components/ui/input';
import {
  Label
} from 'shared/components/ui/label';
import {
  Switch
} from 'shared/components/ui/switch';
import {
  Checkbox
} from 'shared/components/ui/checkbox';
import {
  Context
} from 'shared/context/GlobalState';
import {
  CheckCircle, MailIcon, ServerIcon
} from 'lucide-react';
import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import {
  useParams
} from 'react-router-dom';
import AssistantSelect from '../AssistantSelect';
import {
  ChannelSetupLayout
} from '../ChannelSetupLayout';
import {
  Channel
} from '../../types/builderChannel.types.js';
import {
  MailProviderStatus
} from 'shared/api/services/BEX/mailConfig.service';

interface SmtpImapSetupProps {
  setOpenEmailModal: (open: boolean) => void;
  setEmailChannels: React.Dispatch<React.SetStateAction<Channel[]>>;
  onBack: () => void;
  newChannelObj?: { assistant_id?: string; isAssistantDisabled?: boolean };
}

/**
 * SMTP/IMAP email channel setup — the bex-mail exchange (provider "smtp").
 *
 * Unlike the Outlook path there is no OAuth redirect: the mailbox is described
 * (hosts/ports on the channel document) and its sign-in stored separately in
 * the org's Vault, so rotating a password never touches the channel. Only the
 * org super admin can store credentials — the server enforces that; this form
 * just surfaces the 403 honestly.
 */
const SmtpImapSetup = ({
  setOpenEmailModal,
  setEmailChannels,
  onBack,
  newChannelObj,
}: SmtpImapSetupProps) => {
  const {
    oragID, subOragID
  } = useParams<{ oragID: string; subOragID: string }>();
  const {
    addNewNotifcation
  } = useContext(Context);

  const [btnLoading, setBtnLoading] = useState(false);
  const [existing, setExisting] = useState<{ smtp: MailProviderStatus | null; imap: MailProviderStatus | null }>({
    smtp: null,
    imap: null,
  });

  const [form, setForm] = useState({
    address: '',
    fromName: '',
    topic: '',
    assistantId: newChannelObj?.assistant_id || '',
    smtpHost: '',
    smtpPort: '465',
    imapHost: '',
    imapPort: '993',
    mailbox: 'INBOX',
    receiveEnabled: true,
    // off = listener: inbound is archived and bound, replies stay human/app
    // driven. on = webhook: the assistant runs on each inbound mail.
    runAssistant: false,
    username: '',
    password: '',
    imapSameSignIn: true,
    imapUsername: '',
    imapPassword: '',
  });

  const set = <K extends keyof typeof form>(key: K, value: typeof form[K]) =>
    setForm(prev => ({
      ...prev,
      [key]: value
    }));

  useEffect(() => {
    if (!oragID) return;
    mailConfigService.getMailConfig(oragID)
      .then((res) => setExisting({
        smtp: res.data?.smtp ?? null,
        imap: res.data?.imap ?? null,
      }))
      // 403 = not super admin; they can still create the channel if the
      // credentials were stored by someone who is.
      .catch(() => setExisting({
        smtp: null,
        imap: null
      }));
  }, [oragID]);

  // Running an assistant only means something while we are READING mail, so the
  // effective value is derived rather than read straight from state. The raw
  // flag is kept so switching receive off and back on restores what the user
  // chose — but nothing renders, validates or submits from it directly:
  // toggling receive off used to hide this switch while leaving the flag true,
  // which left the assistant picker on screen, blocked submit until an
  // assistant was chosen, and sent an assistant_id on a send-only channel.
  const runAssistant = form.receiveEnabled && form.runAssistant;

  const credentialsTyped = form.username.trim() !== '' || form.password.trim() !== '';
  // Typing replaces; blank falls back to what is stored. Receiving needs the
  // IMAP credential to actually exist by the end, whichever path supplies it.
  const smtpCredReady =
    (form.username.trim() !== '' && form.password.trim() !== '') ||
    (!credentialsTyped && Boolean(existing.smtp?.passSet));
  const imapCredReady = !form.receiveEnabled || (
    credentialsTyped
      ? (form.imapSameSignIn || (form.imapUsername.trim() !== '' && form.imapPassword.trim() !== ''))
      : Boolean(existing.imap?.passSet)
  );

  const canSubmit =
    /\S+@\S+\.\S+/.test(form.address) &&
    form.topic.trim() !== '' &&
    form.smtpHost.trim() !== '' &&
    (!form.receiveEnabled || form.imapHost.trim() !== '') &&
    (!runAssistant || form.assistantId !== '') &&
    smtpCredReady && imapCredReady;

  const handleSubmit = async () => {
    setBtnLoading(true);
    try {
      // 1 — credentials first: a channel whose sign-in was refused should not
      // exist half-configured.
      if (form.username.trim() && form.password.trim()) {
        try {
          await mailConfigService.addMailSecrets({
            organizationId: oragID || '',
            provider: 'smtp',
            user: form.username.trim(),
            pass: form.password,
          });
          if (form.receiveEnabled) {
            await mailConfigService.addMailSecrets({
              organizationId: oragID || '',
              provider: 'imap',
              user: (form.imapSameSignIn ? form.username : form.imapUsername).trim(),
              pass: form.imapSameSignIn ? form.password : form.imapPassword,
            });
          }
        } catch (error: any) {
          if (error?.response?.status === 403) {
            addNewNotifcation('Only the organization super admin can store mailbox credentials.', 'danger');
          } else {
            addNewNotifcation('Failed to store mailbox credentials.', 'danger');
          }
          return;
        }
      }

      // 2 — the channel document (nothing secret on it)
      const res = await channelService.addChannel({
        org_id: oragID || '',
        sub_org_id: subOragID || '',
        address: form.address.trim(),
        topic: form.topic.trim(),
        type: 'email',
        provider: 'smtp',
        assistant_id: runAssistant ? form.assistantId : '',
        receive_enabled: form.receiveEnabled,
        // assistant off = sends are app/opcode-driven ("tool"); the assistant
        // deciding its own replies ("auto") is a later, deliberate opt-in via
        // channel settings, not a setup default.
        send_mode: 'tool',
        fromName: form.fromName.trim() || undefined,
        smtpHost: form.smtpHost.trim(),
        smtpPort: Number(form.smtpPort) || undefined,
        ...(form.receiveEnabled ? {
          imapHost: form.imapHost.trim(),
          imapPort: Number(form.imapPort) || undefined,
          mailbox: form.mailbox.trim() || undefined,
          webhookMode: runAssistant ? 'webhook' as const : 'listener' as const,
        } : {}),
      });
      addNewNotifcation('Channel added successfully', 'success');
      setOpenEmailModal(false);
      setEmailChannels(prev => [res.data, ...prev]);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 409) {
        addNewNotifcation('Channel already exists', 'danger');
      } else if (status === 403) {
        addNewNotifcation(
          error?.response?.data?.message || 'Resource limit reached. You cannot create more channels under your current plan.',
          'danger'
        );
      } else if (status === 400 || status === 422) {
        addNewNotifcation(error?.response?.data?.message || 'The mailbox configuration was rejected. Check the hosts and try again.', 'danger');
      } else {
        addNewNotifcation('Something went wrong', 'danger');
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const fieldLabel = 'text-[11px] font-semibold text-muted-foreground ml-1';

  return (
      <ChannelSetupLayout
      onCancel={() => setOpenEmailModal(false)}
      onSubmit={handleSubmit}
      submitDisabled={!canSubmit}
      loading={btnLoading}
      onBack={onBack}
      title="Configure Email Channel"
      isDashboard={true}
    >
          <div className="flex flex-col sm:flex-row w-full">
              <aside className="sm:w-[320px] shrink-0 bg-muted/40 border-b sm:border-b-0 sm:border-r border-border p-6 flex flex-col gap-5">
                  <div className="space-y-1">
                      <div className="inline-flex items-center gap-2 px-2 py-1 bg-primary/10 rounded-md text-primary mb-1">
                          <ServerIcon size={12} />
                          <span className="text-[10px] font-bold uppercase tracking-wider">SMTP / IMAP Mailbox</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground px-1">Configure Instance</h3>
                  </div>
                  <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-1">
                          <Label className={fieldLabel}>
                              Display Name <span className="text-red-500">*</span>
                          </Label>
                          <Input
                value={form.topic}
                onChange={(e) => set('topic', e.target.value)}
                placeholder="Sales Mailbox"
                className="h-9 text-xs"
              />
                      </div>
                      <div className="flex items-center justify-between px-1">
                          <Label className="text-[11px] font-semibold text-muted-foreground">
                              Receive replies
                          </Label>
                          <Switch
                checked={form.receiveEnabled}
                onCheckedChange={(v: boolean) => set('receiveEnabled', v)}
              />
                      </div>
                      {form.receiveEnabled && (
                      <div className="flex items-center justify-between px-1">
                          <Label className="text-[11px] font-semibold text-muted-foreground">
                              Run AI assistant on inbound mail
                          </Label>
                          <Switch
                checked={runAssistant}
                onCheckedChange={(v: boolean) => set('runAssistant', v)}
              />
                      </div>
                      )}
                      {runAssistant && !newChannelObj?.isAssistantDisabled && (
                      <div className="flex flex-col gap-1.5">
                          <Label className={fieldLabel}>
                              AI Assistant <span className="text-red-500">*</span>
                          </Label>
                          <AssistantSelect
                className="mt-0.5"
                value={form.assistantId}
                onChange={(value: string) => set('assistantId', value)}
              />
                      </div>
                      )}
                      {!runAssistant && (
                      <p className="text-[11px] text-muted-foreground px-1">
                          {form.receiveEnabled
                            ? 'Inbound mail is stored and matched to what you sent; your app or workflows decide any reply.'
                            : 'Send-only: this mailbox never reads inbound mail.'}
                      </p>
                      )}
                  </div>
              </aside>

              <main className="flex-grow bg-background p-8">
                  <div className="w-full max-w-md mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
                      <div className="space-y-3">
                          <h4 className="text-sm font-bold text-foreground">Mailbox</h4>
                          <div className="grid grid-cols-2 gap-3">
                              <div className="col-span-2 flex flex-col gap-1">
                                  <Label className={fieldLabel}>Email address <span className="text-red-500">*</span></Label>
                                  <Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="sales@yourcompany.com" className="h-9 text-xs" />
                              </div>
                              <div className="col-span-2 flex flex-col gap-1">
                                  <Label className={fieldLabel}>From name</Label>
                                  <Input value={form.fromName} onChange={(e) => set('fromName', e.target.value)} placeholder="Your Company Sales" className="h-9 text-xs" />
                              </div>
                              <div className="flex flex-col gap-1">
                                  <Label className={fieldLabel}>SMTP host <span className="text-red-500">*</span></Label>
                                  <Input value={form.smtpHost} onChange={(e) => set('smtpHost', e.target.value)} placeholder="smtp.yourhost.com" className="h-9 text-xs" />
                              </div>
                              <div className="flex flex-col gap-1">
                                  <Label className={fieldLabel}>SMTP port</Label>
                                  <Input value={form.smtpPort} onChange={(e) => set('smtpPort', e.target.value)} className="h-9 text-xs" />
                              </div>
                              {form.receiveEnabled && (
                              <>
                                  <div className="flex flex-col gap-1">
                                      <Label className={fieldLabel}>IMAP host <span className="text-red-500">*</span></Label>
                                      <Input value={form.imapHost} onChange={(e) => set('imapHost', e.target.value)} placeholder="imap.yourhost.com" className="h-9 text-xs" />
                                  </div>
                                  <div className="flex flex-col gap-1">
                                      <Label className={fieldLabel}>IMAP port</Label>
                                      <Input value={form.imapPort} onChange={(e) => set('imapPort', e.target.value)} className="h-9 text-xs" />
                                  </div>
                                  <div className="col-span-2 flex flex-col gap-1">
                                      <Label className={fieldLabel}>Folder</Label>
                                      <Input value={form.mailbox} onChange={(e) => set('mailbox', e.target.value)} className="h-9 text-xs" />
                                  </div>
                              </>
                              )}
                          </div>
                      </div>

                      <div className="space-y-3">
                          <div className="flex items-center gap-2">
                              <h4 className="text-sm font-bold text-foreground">Sign-in</h4>
                              {existing.smtp?.passSet && !credentialsTyped && (
                              <span className="inline-flex items-center gap-1 text-[11px] text-green-600">
                                  <CheckCircle size={12} /> credential stored{existing.smtp.user ? ` for ${existing.smtp.user}` : ''}
                              </span>
                              )}
                          </div>
                          <p className="text-[11px] text-muted-foreground">
                              Stored securely for this organization, separate from the channel — only the
                              org super admin can change it, and it is never shown again.
                              {existing.smtp?.passSet ? ' Leave blank to keep the stored sign-in.' : ''}
                          </p>
                          <div className="grid grid-cols-2 gap-3">
                              <div className="flex flex-col gap-1">
                                  <Label className={fieldLabel}>Username {!existing.smtp?.passSet && <span className="text-red-500">*</span>}</Label>
                                  <Input value={form.username} onChange={(e) => set('username', e.target.value)} placeholder={existing.smtp?.user || 'sales@yourcompany.com'} className="h-9 text-xs" autoComplete="off" />
                              </div>
                              <div className="flex flex-col gap-1">
                                  <Label className={fieldLabel}>Password {!existing.smtp?.passSet && <span className="text-red-500">*</span>}</Label>
                                  <Input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} className="h-9 text-xs" autoComplete="new-password" />
                              </div>
                          </div>
                          {form.receiveEnabled && credentialsTyped && (
                          <>
                              <div className="flex items-center gap-2 px-1">
                                  <Checkbox
                    id="imap-same"
                    checked={form.imapSameSignIn}
                    onCheckedChange={(v: boolean) => set('imapSameSignIn', Boolean(v))}
                  />
                                  <Label htmlFor="imap-same" className="text-[11px] text-muted-foreground">
                                      IMAP uses the same sign-in
                                  </Label>
                              </div>
                              {!form.imapSameSignIn && (
                              <div className="grid grid-cols-2 gap-3">
                                  <div className="flex flex-col gap-1">
                                      <Label className={fieldLabel}>IMAP username</Label>
                                      <Input value={form.imapUsername} onChange={(e) => set('imapUsername', e.target.value)} className="h-9 text-xs" autoComplete="off" />
                                  </div>
                                  <div className="flex flex-col gap-1">
                                      <Label className={fieldLabel}>IMAP password</Label>
                                      <Input type="password" value={form.imapPassword} onChange={(e) => set('imapPassword', e.target.value)} className="h-9 text-xs" autoComplete="new-password" />
                                  </div>
                              </div>
                              )}
                          </>
                          )}
                      </div>

                      <div className="flex items-start gap-2 p-3 bg-muted border border-border rounded-lg">
                          <MailIcon size={14} className="text-muted-foreground shrink-0 mt-0.5" />
                          <p className="text-[11px] text-muted-foreground">
                              Before emailing real recipients, publish SPF, DKIM and DMARC for the sending
                              domain — without them mail lands in spam and replies never come back.
                          </p>
                      </div>
                  </div>
              </main>
          </div>
      </ChannelSetupLayout>
  );
};

export default SmtpImapSetup;
